// Audio con Web Audio API (sin archivos): efectos sintetizados + música de fondo ambiental.
// Aislado del gameplay para poder sustituirlo por assets reales en el futuro.

export class AudioManager {
  constructor(muted = false) {
    this.ctx = null;
    this.muted = muted;
    this.musicTimer = null;
    this.musicStep = 0;
    this.masterGain = null;
    this.musicGain = null;
    this.victoryGain = null;
    this.victoryNotes = [];
    this.victoryTimer = null;
    this.unlockPromise = null;
    this.unlockListenersInstalled = false;
    this.unlockHandler = null;
  }

  isIOSAudioState(state) {
    return state === 'suspended' || state === 'interrupted';
  }

  prepareIOSAudioSession() {
    if (typeof navigator === 'undefined') return;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    // En iOS, Web Audio puede ir al canal de timbre y quedar mudo aunque el
    // contexto esté activo. playback solo se solicita cuando WebKit expone la API.
    if (!isIOS || !navigator.audioSession) return;
    try {
      navigator.audioSession.type = 'playback';
    } catch (error) {
      // API opcional: navegadores sin soporte conservan el flujo existente.
    }
  }

  createContext() {
    if (this.ctx) return this.ctx;
    this.prepareIOSAudioSession();
    const AC = window.AudioContext || window.webkitAudioContext;
    this.ctx = new AC();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = this.muted ? 0 : 0.9;
    this.masterGain.connect(this.ctx.destination);
    this.musicGain = this.ctx.createGain();
    this.musicGain.gain.value = 0.12;
    this.musicGain.connect(this.masterGain);

    // iOS puede suspender Web Audio al volver de otra app o pestaña.
    // El listener temporal vuelve a habilitarse únicamente mientras haga falta.
    this.ctx.onstatechange = () => {
      if (this.isIOSAudioState(this.ctx?.state)) this.installUnlockListeners();
      else if (this.ctx?.state === 'running') this.removeUnlockListeners();
    };
    return this.ctx;
  }

  installUnlockListeners() {
    if (this.unlockListenersInstalled || typeof window === 'undefined') return;
    this.unlockHandler = () => { this.unlock(); };
    ['pointerdown', 'touchstart', 'click'].forEach(eventName => {
      window.addEventListener(eventName, this.unlockHandler, { passive: true, capture: true });
    });
    this.unlockListenersInstalled = true;
  }

  removeUnlockListeners() {
    if (!this.unlockListenersInstalled || typeof window === 'undefined') return;
    ['pointerdown', 'touchstart', 'click'].forEach(eventName => {
      window.removeEventListener(eventName, this.unlockHandler, true);
    });
    this.unlockHandler = null;
    this.unlockListenersInstalled = false;
  }

  unlock() {
    const ctx = this.createContext();
    if (!ctx || ctx.state === 'closed') return Promise.resolve(false);
    if (ctx.state === 'running') {
      this.removeUnlockListeners();
      return Promise.resolve(true);
    }
    // WebKit/iPhone también puede usar el estado no estándar interrupted.
    if (!this.isIOSAudioState(ctx.state)) {
      this.installUnlockListeners();
      return Promise.resolve(false);
    }
    if (this.unlockPromise) return this.unlockPromise;

    this.unlockPromise = Promise.resolve(ctx.resume())
      .catch(() => false)
      .then(() => {
        const running = ctx.state === 'running';
        this.unlockPromise = null;
        if (running) {
          this.removeUnlockListeners();
          // Solo después de comprobar running iniciamos la música. Así no se
          // crea un musicTimer mudo en Safari.
          if (!this.muted) this.startMusic();
        } else {
          this.installUnlockListeners();
        }
        return running;
      });
    return this.unlockPromise;
  }

  ensure() {
    const ctx = this.createContext();
    if (this.isIOSAudioState(ctx.state)) this.unlock();
    return ctx;
  }

  blip(freq, dur, type = 'sine', vol = 0.2, when = 0, dest = null) {
    if (this.muted) return;
    this.ensure();
    const t = this.ctx.currentTime + when;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(dest || this.masterGain);
    o.start(t);
    o.stop(t + dur + 0.03);
    o.onended = () => { o.disconnect(); g.disconnect(); };
    return o;
  }

  slide(f1, f2, dur, type = 'sine', vol = 0.2, when = 0) {
    if (this.muted) return;
    this.ensure();
    const t = this.ctx.currentTime + when;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(f1, t);
    o.frequency.exponentialRampToValueAtTime(Math.max(1, f2), t + dur);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.masterGain);
    o.start(t);
    o.stop(t + dur + 0.03);
  }

  playJump() {
    this.slide(340, 660, 0.16, 'square', 0.14);
  }

  // Confirmación original de acierto: dos notas ascendentes, breves y claras.
  // Las rachas usan una firma sonora mayor, sin detener la partida.
  playCorrect(streak = 1) {
    this.blip(659.25, 0.14, 'triangle', 0.3);
    this.blip(987.77, 0.22, 'triangle', 0.28, 0.1);

    if (streak === 3) {
      // Racha inicial: una tercera nota brillante.
      this.blip(1318.51, 0.18, 'sine', 0.24, 0.22);
    } else if (streak === 5) {
      // Racha destacada: mini-fanfarria cósmica, aún menor de medio segundo.
      [[1046.5, 0.18], [1318.51, 0.28], [1567.98, 0.4]].forEach(([freq, when]) => {
        this.blip(freq, 0.18, 'triangle', 0.25, when);
      });
    }
  }

  playWrong() {
    this.slide(320, 110, 0.4, 'sawtooth', 0.16);
  }

  playCollect() {
    this.blip(880, 0.1, 'triangle', 0.16);
    this.blip(1320, 0.12, 'triangle', 0.12, 0.06);
  }

  playLand() {
    this.blip(180, 0.08, 'sine', 0.1);
  }

  // Cierre breve de misión: baliza, aterrizaje y despegue de la nave.
  // Es distinto al sonido de respuesta correcta, sin detener la jugabilidad.
  playDeparture() {
    this.blip(659.25, 0.16, 'triangle', 0.2);
    this.blip(880, 0.2, 'triangle', 0.18, 0.16);
    // Descenso suave; el aterrizaje se confirma en LevelScene con playLand().
    this.slide(420, 180, 0.45, 'sine', 0.09, 0.34);
    // Impulso final cuando la nave parte hacia la próxima misión,
    // sincronizado después de que el personaje entra y la puerta se cierra.
    this.slide(210, 980, 0.72, 'sine', 0.16, 2.1);
    this.blip(1318.51, 0.18, 'triangle', 0.14, 2.44);
  }

  playVictory() {
    this.stopVictory(false);
    if (this.muted) return;
    this.ensure();
    this.stopMusic();
    this.musicGain.gain.setValueAtTime(0, this.ctx.currentTime);
    this.victoryGain = this.ctx.createGain();
    this.victoryGain.connect(this.masterGain);
    // Fanfarria original: llamada ascendente y resolución en do mayor.
    const melody = [
      [523.25, 0, 0.18], [523.25, 0.22, 0.18], [523.25, 0.44, 0.18],
      [659.25, 0.68, 0.3], [783.99, 1.02, 0.3], [1046.5, 1.38, 0.55],
      [987.77, 2.02, 0.2], [1174.66, 2.26, 0.2], [1046.5, 2.52, 1.1],
    ];
    const note = (frequency, start, duration, type, volume) => {
      this.victoryNotes.push(this.blip(frequency, duration, type, volume, start, this.victoryGain));
    };
    melody.forEach(([f, start, duration]) => note(f, start, duration, 'triangle', 0.2));
    [[0, [130.81, 261.63, 329.63]], [1.02, [196, 392, 493.88]],
      [2.52, [130.81, 261.63, 329.63, 392]]].forEach(([start, chord]) => {
      chord.forEach(f => note(f, start, start === 2.52 ? 1.15 : 0.65, 'sine', 0.065));
    });
    this.victoryTimer = setTimeout(() => this.stopVictory(), 3800);
  }

  stopVictory(resumeMusic = true) {
    const wasPlaying = this.victoryGain !== null;
    clearTimeout(this.victoryTimer);
    this.victoryTimer = null;
    this.victoryNotes.forEach(note => { if (note) note.stop(); });
    this.victoryNotes = [];
    if (this.victoryGain) this.victoryGain.disconnect();
    this.victoryGain = null;
    if (this.musicGain) this.musicGain.gain.setValueAtTime(0.12, this.ctx.currentTime);
    if (wasPlaying && resumeMusic && !this.muted) this.startMusic();
  }

  startMusic() {
    if (this.muted || this.victoryGain) return;
    const ctx = this.ensure();
    // PC/tablet normalmente llegan aquí en running; iOS espera al unlock
    // real antes de crear el temporizador musical.
    if (!ctx || ctx.state !== 'running') {
      this.installUnlockListeners();
      return;
    }
    if (this.musicTimer) return;
    // Patrón pentatónico suave y espacial en bucle.
    const bass = [110, 110, 146.83, 130.81];
    const arp = [440, 554.37, 659.25, 554.37, 493.88, 659.25, 587.33, 493.88];
    this.musicStep = 0;
    this.musicTimer = setInterval(() => {
      if (this.muted) return;
      const s = this.musicStep;
      if (s % 4 === 0) this.blip(bass[(s / 4) % bass.length], 0.9, 'sine', 0.18, 0, this.musicGain);
      this.blip(arp[s % arp.length], 0.35, 'triangle', 0.09, 0, this.musicGain);
      this.musicStep = (s + 1) % 64;
    }, 260);
  }

  stopMusic() {
    if (this.musicTimer) {
      clearInterval(this.musicTimer);
      this.musicTimer = null;
    }
  }

  setMuted(m) {
    this.muted = m;
    if (this.masterGain) this.masterGain.gain.setValueAtTime(m ? 0 : 0.9, this.ctx.currentTime);
    if (m) {
      this.stopVictory(false);
      this.stopMusic();
    }
    else this.startMusic();
    return this.muted;
  }

  toggleMute() {
    return this.setMuted(!this.muted);
  }
}
